<template>
  <Row>
    <Row>
      <div id="map-border">
        <div id="map"></div>
      </div>
    </Row>
    <Row>
      <GeolocationAutoComplete
        :iso="iso"
        @select="addLocation"
        @focus="placeSuggestionMarker"
        @blur="removeSuggestionMarker"
      ></GeolocationAutoComplete>
    </Row>

    <Row>
      <!-- <h3>Locations</h3> -->
      <div class="action-buttons">
        <i-button size="small" @click="findBestZoom">Best fit</i-button>
        <i-button size="small" @click="fitToCountry">Fit to Country</i-button>
        <i-button size="small" @click="drawCentroid">Draw Centroid</i-button>
      </div>
      <AgGridVue
        style="width: 100%; height: 210px;"
        class="ag-theme-balham"
        :gridOptions="locationsGridOptions"
        :columnDefs="locationsColumnDefs"
        :rowData="value.locations"
      ></AgGridVue>
    </Row>
  </Row>
</template>

<script>
import axios from "axios";
import { AgGridVue } from "ag-grid-vue";
import "leaflet-extra-markers/dist/css/leaflet.extra-markers.min.css";
import "leaflet-extra-markers";
import { getGeoEntityByISO3 } from "@/models/GeoEntity.ts";
import GeolocationAutoComplete from "@/components/geolocation_autocomplete.vue";

// -----------------------------------------------------------
// Map related variables
// -----------------------------------------------------------
const L = window.L;
let map;
let centroidMarker;
let focusMarker;
// -----------------------------------------------------------
// Icon marker classes
// -----------------------------------------------------------
let displacementIconMarker = L.ExtraMarkers.icon({
  icon: "fa-child",
  markerColor: "blue",
  shape: "circle",
  prefix: "fa",
});

let focusIconMarker = L.ExtraMarkers.icon({
  icon: "fa-flag",
  markerColor: "yellow",
  shape: "circle",
  prefix: "fa",
});

let centroidIconMarker = L.ExtraMarkers.icon({
  icon: "fa-crosshairs",
  markerColor: "orange",
  shape: "circle",
  prefix: "fa",
});

let errorIconMarker = L.ExtraMarkers.icon({
  icon: "fa-warning",
  markerColor: "red",
  shape: "square",
  prefix: "fa",
});

// -----------------------------------------------------------
export default {
  components: { GeolocationAutoComplete, AgGridVue },
  props: {
    value: {
      type: Object,
      required: true,
      default: () => {
        return {
          centroid: [],
          locations: [],
        };
      },
    },
    iso3: { type: String, default: "" },
  },
  data() {
    return {
      country: null,
      markers: [],
      options: [],
      iso: "",
      loading: false,
      locationsGridOptions: {
        defaultColDef: {
          sortable: true,
          resizable: true,
        },
        enableRangeSelection: true,
        rowSelection: "single",
        onCellMouseOver: this.placeLocationMarker,
        onCellMouseOut: this.removeLocationMarker,
        onFirstDataRendered: params => {
          params.api.sizeColumnsToFit();
        },
        getContextMenuItems: params => [
          {
            name: "Delete",
            action: () => {
              this.removeLocation(params.node.id);
            },
            cssClasses: ["deleteMenuItem"],
            icon: '<i class="fa fa-remove"/>',
          },
          "separator",
          ...params.defaultItems,
        ],
      },
      locationsColumnDefs: Object.freeze([
        {
          headerName: "Type",
          width: 10,
          field: "type",
          editable: true,
          cellEditor: "richSelect",
          cellEditorParams: {
            values: ["Origin", "Destination"],
          },
        },
        {
          headerName: "Name",
          width: 40,
          field: "name",
        },
        {
          headerName: "Accuracy",
          width: 10,
          field: "accuracy",
          editable: true,
          cellEditor: "agRichSelectCellEditor",
          cellEditorParams: {
            values: ["Point", "Admin"],
          },
        },
        {
          headerName: "Moved",
          width: 10,
          field: "moved",
          cellRenderer: params => {
            return params.value ? "Yes" : "";
          },
        },
        {
          headerName: "Reported Name",
          width: 30,
          field: "reported_name",
          editable: true,
          cellEditor: "agTextCellEditor",
        },
      ]),
    };
  },
  watch: {
    iso3(iso3) {
      this.iso3 = iso3 || "";
      this.country = getGeoEntityByISO3(iso3);
      this.iso = this.country.iso;
      this.findBestZoom();
    },
  },
  mounted() {
    window.mapobj = this;
    map = L.map("map").setView([0.0, 0.0], 2);
    map.scrollWheelZoom.disable();

    L.tileLayer("http://{s}.tile.osm.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);
    this.drawMarkers();
    this.findBestZoom();
  },
  updated() {
    this.drawMarkers();
    this.findBestZoom();
  },
  methods: {
    onRowSelected(value) {
      console.log(value);
    },
    drawMarkers() {
      if (this.markers.length > 0) {
        this.markers.forEach(marker => {
          map.removeLayer(marker);
        });
      }
      this.markers = [];
      this.value.locations.forEach((location, i) => {
        this.addMarker(location, i);
      });
      this.drawCentroid();
    },
    // Draw the centroid of multiple location
    drawCentroid() {
      let country = this.country;
      let centroid = this.value.centroid;
      let locations = this.value.locations;
      let count = locations.length;

      if (this.value.locations.length === 0) {
        // set the default value of the centroid
        if (country) {
          this.value.centroid = country.centroid;
        }
      } else {
        // if there are locations then recalcualte the centroid
        let orgCount = 0;
        if (count > 0) {
          centroid = [0, 0];
          locations.forEach(location => {
            if (location.type === "Origin") {
              orgCount++;
              centroid[0] += location.latlng[0];
              centroid[1] += location.latlng[1];
            }
          });
          centroid[0] = centroid[0] / orgCount;
          centroid[1] = centroid[1] / orgCount;
        }
        this.value.centroid = centroid;
      }

      // Draw the centroid marker
      // 1. remove marker
      if (centroidMarker) {
        map.removeLayer(centroidMarker);
      }
      // 2. Draw marker
      if (count !== 1 && centroid && centroid.length === 2) {
        centroidMarker = L.marker(centroid, {
          icon: centroidIconMarker,
          title: "Centroid",
        });
        centroidMarker.addTo(map);
      }
    },
    addMarker(location) {
      let marker;

      if (location.error) {
        marker = L.marker(location.latlng, {
          icon: errorIconMarker,
          title: location.name,
          draggable: true,
        });
      } else {
        marker = L.marker(location.latlng, {
          icon: displacementIconMarker,
          title: location.name,
          draggable: true,
        });
      }

      marker.on("dragend", e => {
        let url = `https://osmnames.idmcdb.org/r/${e.target._latlng.lng}/${
          e.target._latlng.lat
        }`;
        this.locationsGridOptions.api.showLoadingOverlay();
        axios.get(url).then(response => {
          console.log(this.iso.toLowerCase(), response.data.results[0]);
          location.geolocation = response.data.results[0];
          location.error =
            location.geolocation.country_code.toLowerCase() !==
            this.iso.toLowerCase();
          location.accuracy = "Point";
          location.latlng = [e.target._latlng.lat, e.target._latlng.lng];
          location.moved = true;
          location.name = location.geolocation.display_name;
          location.reported_name = "";
          this.drawMarkers();
          this.locationsGridOptions.api.refreshCells();
          this.locationsGridOptions.api.hideOverlay();
        });
      });
      marker.addTo(map);
      this.markers.push(marker);
    },
    removeLocation(i) {
      i = parseInt(i, 10);
      this.value.locations = this.value.locations.filter(
        (item, index) => index !== i
      );
      map.removeLayer(this.markers[i]);
      this.markers = this.markers.filter((item, index) => index !== i);
      this.findBestZoom();
    },
    placeLocationMarker(event) {
      this.markers[event.rowIndex].setIcon(focusIconMarker);
    },
    removeLocationMarker(event) {
      this.markers[event.rowIndex].setIcon(displacementIconMarker);
    },
    findBestZoom() {
      if (this.markers.length >= 2) {
        let featureGroup = L.featureGroup(this.markers);
        map.fitBounds(featureGroup.getBounds().pad(0.5), { animate: false });
      } else if (this.markers.length === 1) {
        const marker = this.markers[0];
        map.setView(marker.getLatLng(), 12);
      } else {
        this.fitToCountry();
      }
    },
    fitToCountry() {
      if (!!this.country && !!this.country.boundingbox) {
        let bx = [
          [
            parseFloat(this.country.boundingbox[0]),
            parseFloat(this.country.boundingbox[1]),
          ],
          [
            parseFloat(this.country.boundingbox[2]),
            parseFloat(this.country.boundingbox[3]),
          ],
        ];
        map.fitBounds(bx);
      }
    },
    addLocation(suggestion) {
      for (const l of this.value.locations) {
        if (l.geolocation.osm_id === suggestion.osm_id) {
          return;
        }
      }
      const location = {
        type: "Origin",
        name: suggestion.display_name,
        latlng: [suggestion.lat, suggestion.lon],
        accuracy: "Point",
        moved: false,
        geolocation: suggestion,
      };
      this.value.locations.push(location);
      this.addMarker(location, this.value.locations.length - 1);
      this.findBestZoom();
    },
    placeSuggestionMarker(suggestion) {
      if (focusMarker) {
        map.removeLayer(focusMarker);
      }
      focusMarker = L.marker([suggestion.lat, suggestion.lon], {
        icon: focusIconMarker,
        title: "Current",
      });
      focusMarker.addTo(map);
      map.setView(focusMarker.getLatLng(), 12);
    },
    removeSuggestionMarker() {
      if (focusMarker) {
        map.removeLayer(focusMarker);
        this.findBestZoom();
      }
    },
  },
};
</script>

<style lang="css">
#map-border {
  padding: 3px;
  border: 1px solid #dddee1;
  background-color: white;
  border-radius: 3px;
  /* margin: 12px 0; */
  /* margin: 0 12px 0 0; */
}

#map {
  height: 480px;
  border-radius: 3px;
}

.tags {
  margin: 6px 0;
  padding: 3px 6px;
  border: 1px solid #ccc;
  border-radius: 3px;
}

.ajax-field input {
  width: 100%;
}

.ajax-field .menu {
  border: 1px solid #ddd;
  border-radius: 0 0 3px 3px;
  position: absolute;
  background-color: white;
  z-index: 999999;
  height: 328px;
  overflow: scroll;
  line-height: 1.3em;
  box-shadow: 0px 1px 3px #eee;
}

.ajax-field .menu .item {
  padding: 6px 6px 6px 6px;
}

.ajax-field .menu .item:hover {
  cursor: pointer;
  background-color: #eee;
}

.deleteMenuItem {
  color: #cc0000;
}
</style>
